import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LabelList,
  ResponsiveContainer,
} from 'recharts'
import styled from 'styled-components'
import { SPACING, TYPOGRAPHY } from '../../../../styles/theme'
import {
  INSIGHT_PRIMARY,
  INSIGHT_PRIMARY_HOVER,
  INSIGHT_CURSOR_FILL,
  INSIGHT_TOOLTIP_STYLE,
} from './insightColors'

interface MultipleChoiceChartProps {
  answers: string[]
  options: string[]
  isPrinting: boolean
}

const NoData = styled.div`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-secondary);
  text-align: center;
  padding: ${SPACING.xl} 0;
`

export function MultipleChoiceChart({ answers, options, isPrinting }: MultipleChoiceChartProps) {
  if (answers.length === 0) {
    return <NoData>No responses yet</NoData>
  }

  const freq: Record<string, number> = {}
  options.forEach((o) => (freq[o] = 0))
  answers.forEach((a) => {
    if (a in freq) freq[a]++
  })

  const data = Object.entries(freq)
    .map(([option, count]) => ({
      option: option.length > 28 ? option.slice(0, 26) + '…' : option,
      fullOption: option,
      count,
      pct: answers.length > 0 ? Math.round((count / answers.length) * 100) : 0,
    }))
    .sort((a, b) => b.count - a.count)

  const barHeight = 32
  const chartHeight = Math.max(120, data.length * barHeight + 40)

  return (
    <ResponsiveContainer width="100%" height={chartHeight}>
      <BarChart data={data} layout="vertical" margin={{ top: 0, right: 48, left: 8, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="var(--color-border)" />
        <XAxis
          type="number"
          domain={[0, 100]}
          tickFormatter={(v) => `${v}%`}
          tick={{ fontSize: TYPOGRAPHY.caption.fontSize }}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          type="category"
          dataKey="option"
          width={110}
          tick={{ fontSize: TYPOGRAPHY.caption.fontSize }}
          axisLine={false}
          tickLine={false}
        />
        {!isPrinting && (
          <Tooltip
            cursor={{ fill: INSIGHT_CURSOR_FILL }}
            {...INSIGHT_TOOLTIP_STYLE}
            labelFormatter={(_label, payload) =>
              (payload as { payload?: { fullOption?: string } }[])[0]?.payload?.fullOption ?? _label
            }
            formatter={(
              _val: unknown,
              _name: unknown,
              props: { payload?: { count?: number; pct?: number } }
            ) => [`${props.payload?.count ?? 0} responses (${props.payload?.pct ?? 0}%)`, '']}
          />
        )}
        <Bar
          dataKey="pct"
          fill={INSIGHT_PRIMARY}
          activeBar={{ fill: INSIGHT_PRIMARY_HOVER }}
          radius={[0, 3, 3, 0]}
          isAnimationActive={!isPrinting}
        >
          <LabelList
            dataKey="pct"
            position="right"
            formatter={(v: string | number | boolean | null | undefined) =>
              v != null ? `${v}%` : ''
            }
            style={{ fontSize: TYPOGRAPHY.caption.fontSize, fill: 'var(--color-text-secondary)' }}
          />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  )
}
